import { DEFAULT_LANGUAGE, isLanguage, type Language } from './languages';
import { isValidGuess } from './words';

/**
 * Challenge word obfuscation (spec §4.2): XOR with a fixed key then
 * base64url. A spoiler deterrent, not encryption. Environment-neutral —
 * no Buffer/atob so it runs in browser, worker, and Node alike.
 */
const XOR_KEY = 'lexicon';
const B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function xorBytes(word: string): number[] {
  return [...word].map((ch, i) =>
    ch.charCodeAt(0) ^ XOR_KEY.charCodeAt(i % XOR_KEY.length));
}

function toBase64Url(bytes: number[]): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const [a, b, c] = [bytes[i]!, bytes[i + 1], bytes[i + 2]];
    out += B64URL[a >> 2]! + B64URL[((a & 3) << 4) | ((b ?? 0) >> 4)]!;
    if (b !== undefined) out += B64URL[((b & 15) << 2) | ((c ?? 0) >> 6)]!;
    if (c !== undefined) out += B64URL[c & 63]!;
  }
  return out;
}

function fromBase64Url(s: string): number[] | null {
  if (s.length === 0 || s.length % 4 === 1) return null;
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const ch of s) {
    const v = B64URL.indexOf(ch);
    if (v === -1) return null;
    buffer = (buffer << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }
  // Reject non-canonical encodings: any leftover low-order bits in the
  // final character must be zero, matching what a real encoder emits.
  if (bits > 0 && (buffer & ((1 << bits) - 1)) !== 0) return null;
  return bytes;
}

export function obfuscate(word: string): string {
  return toBase64Url(xorBytes(word.toLowerCase()));
}

export function deobfuscate(s: string): string | null {
  const bytes = fromBase64Url(s);
  if (!bytes || bytes.length === 0) return null;
  return bytes
    .map((b, i) => String.fromCharCode(b ^ XOR_KEY.charCodeAt(i % XOR_KEY.length)))
    .join('');
}

/**
 * Share URL: server code path + offline fragment fallback.
 *
 * A non-default language adds `&l=<tag>` to the fragment, so a link that
 * never reaches the server still says which word list the word belongs to.
 * An English link is unchanged, character for character, from before Dutch
 * existed — every already-shared link keeps working and keeps meaning the
 * same thing.
 */
export function buildChallengeUrl(
  base: string,
  code: string,
  word: string,
  language: Language = DEFAULT_LANGUAGE,
): string {
  const tag = language === DEFAULT_LANGUAGE ? '' : `&l=${language}`;
  return `${base}/c/${code}#w=${obfuscate(word)}${tag}`;
}

/** Playable lengths (spec §10.1); the dictionary check is the caller's. */
const FRAGMENT_WORD_RE = /^[a-z]{4,7}$/;

/**
 * Extract the challenge word from a URL fragment; null if absent or
 * malformed.
 *
 * `isValid` defaults to the 5-letter accepted list, so every existing
 * caller behaves exactly as before. The web passes its own: it cannot
 * dictionary-check a 4/6/7-letter word until that length's list has been
 * lazily loaded, and choosing which list to load needs the word first.
 */
export function parseFragmentWord(
  hash: string,
  isValid: (word: string) => boolean = isValidGuess,
): string | null {
  const frag = hash.startsWith('#') ? hash.slice(1) : hash;
  const match = /(?:^|&)w=([^&]+)/.exec(frag);
  if (!match) return null;
  const word = deobfuscate(match[1]!);
  if (word === null || !FRAGMENT_WORD_RE.test(word)) return null;
  return isValid(word) ? word : null;
}

/**
 * The language tag from a challenge fragment, or null when absent or
 * unrecognised. Null is not an error: every link written before the
 * language existed is an English link, and a caller that gets null falls
 * back to identifying the language from the word itself
 * (`loadLengthAccepting`).
 */
export function parseFragmentLanguage(hash: string): Language | null {
  const frag = hash.startsWith('#') ? hash.slice(1) : hash;
  const match = /(?:^|&)l=([^&]+)/.exec(frag);
  if (!match) return null;
  const tag = match[1]!.toLowerCase();
  return isLanguage(tag) ? tag : null;
}
